import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import regionsCulturellesData from '../data/regionsCulturellesData.json';
import SectionModal from './SectionModal';

// Déclaration globale pour Google Maps
declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

const MapContainer = styled.div`
  width: 100%;
  height: 80vh;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const MapTitle = styled.h2`
  position: absolute;
  top: 20px;
  left: 20px;
  color: ${theme.colors.outchou_green};
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  font-family: ${theme.fonts.title};
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  background: rgba(255, 255, 255, 0.9);
  padding: 10px 15px;
  border-radius: 8px;
`;

const Legend = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-height: 60vh;
  overflow-y: auto;
  z-index: 1000;
  max-width: 250px;
`;

const LegendTitle = styled.h3`
  color: ${theme.colors.outchou_green};
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  font-family: ${theme.fonts.title};
`;

const LegendItem = styled.div<{ color: string; isHovered: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  background-color: ${props => props.isHovered ? '#f0f0f0' : 'transparent'};
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

const LegendColor = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  background-color: ${props => props.color};
  border-radius: 50%;
  margin-right: 10px;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const LegendName = styled.span`
  font-size: 12px;
  color: #333;
  font-weight: 500;
  font-family: ${theme.fonts.title};
`;

// Fonction pour obtenir l'image d'une région culturelle
const getRegionImage = (regionId: number): string => {
  const region = regionsCulturellesData.regions.find(r => r.id === regionId);
  const imageUrl = region?.imageUrl || '/shared_assets/images/relief/le tell.jpg';
  // Ajouter un paramètre de version pour éviter le cache (basé sur la date de modification)
  return imageUrl + '?v=' + Date.now();
};

// Fonction pour obtenir la légende d'une région culturelle
const getRegionImageCaption = (regionId: number): string | undefined => {
  // TODO: ajouter les légendes pour les images culturelles
  return undefined;
};

// Fonction pour convertir les données de région culturelle en format sections pour le modal
const convertRegionToSections = (region: any): { [key: string]: string[] | string } => {
  // Utiliser les sections du JSON si elles existent, sinon créer un objet vide
  const sections: { [key: string]: string[] | string } = region.sections ? { ...region.sections } : {};
  
  // Filtrer les sections vides ou undefined
  Object.keys(sections).forEach(key => {
    const value = sections[key];
    if (!value || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0)) {
      delete sections[key];
    }
  });
  
  // Ajouter les wilayas si elles n'existent pas déjà dans les sections
  if (!sections['Wilayas'] && region.wilayas && region.wilayas.length > 0) {
    sections['Wilayas'] = region.wilayas;
  }
  
  return sections;
};

const CulturalRegionsMap: React.FC = () => {
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalWidth, setModalWidth] = useState<number | null>(null);
  const [isPinned, setIsPinned] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [wilayasWithHolesGeoData, setWilayasWithHolesGeoData] = useState<any>(null);
  const [exceptionDairasGeoData, setExceptionDairasGeoData] = useState<any>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const isPinnedRef = useRef(false);
  
  // Créer un Map des régions par ID pour lookup O(1)
  const regionsMap = useMemo(() => {
    const map = new Map<number, any>();
    regionsCulturellesData.regions.forEach(region => {
      map.set(region.id, region);
    });
    return map;
  }, []);

  // Charger les fichiers GeoJSON pré-calculés pour les régions culturelles
  useEffect(() => {
    const timestamp = Date.now();
    
    // Charger wilayas_cultural_with_holes.geojson
    fetch(`/data/wilayas_cultural_with_holes.geojson?_=${timestamp}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setWilayasWithHolesGeoData(data);
      })
      .catch(error => {
        console.error('Erreur lors du chargement de wilayas_cultural_with_holes.geojson:', error);
      });

    // Charger exception_dairas_cultural.geojson
    fetch(`/data/exception_dairas_cultural.geojson?_=${timestamp}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setExceptionDairasGeoData(data);
      })
      .catch(error => {
        console.error('Erreur lors du chargement de exception_dairas_cultural.geojson:', error);
      });
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Charger Google Maps API
    if (!window.google && !document.querySelector('script[src*="maps.googleapis.com"]')) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDgmbAXJ9fkmwRlYvg7ygBLsgJ6TP2bp1w&libraries=geometry`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      
      script.onload = () => {
        if (wilayasWithHolesGeoData && exceptionDairasGeoData) {
          initMap();
        }
      };
    } else if (window.google && wilayasWithHolesGeoData && exceptionDairasGeoData) {
      initMap();
    }

    function initMap() {
      if (mapRef.current && wilayasWithHolesGeoData && exceptionDairasGeoData) {
        if (mapInstance.current) {
          mapInstance.current.data.forEach((feature: any) => {
            mapInstance.current.data.remove(feature);
          });
          mapInstance.current = null;
        }
        
        mapInstance.current = new window.google.maps.Map(mapRef.current, {
          center: { lat: 28.0339, lng: 1.6596 },
          zoom: 5,
          mapTypeId: 'satellite',
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          scaleControl: false,
          rotateControl: false,
          panControl: false,
          overviewMapControl: false,
          scrollwheel: true,
          disableDoubleClickZoom: false,
          keyboardShortcuts: true,
          gestureHandling: 'greedy',
          clickableIcons: false,
          styles: [
            {
              featureType: 'all',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });

        // ÉTAPE 1 : Charger les wilayas pré-calculées avec trous
        if (wilayasWithHolesGeoData && regionsCulturellesData && (wilayasWithHolesGeoData as any).features) {
          mapInstance.current.data.addGeoJson(wilayasWithHolesGeoData);
        }
      
        // ÉTAPE 2 : Charger les daïras d'exception pré-calculées
        if (exceptionDairasGeoData && (exceptionDairasGeoData as any).features) {
          mapInstance.current.data.addGeoJson(exceptionDairasGeoData);
        }
      
        let clickHandledByFeature = false;
        
        // ÉTAPE 3 : Appliquer le style UNIFIÉ
        mapInstance.current.data.setStyle(null);
        
        mapInstance.current.data.setStyle((feature: any) => {
          const featureName = feature.getProperty('name') || feature.getProperty('name:fr');
          if (!featureName) {
            return {
              fillColor: 'transparent',
              fillOpacity: 0,
              strokeColor: 'transparent',
              strokeWeight: 0,
              strokeOpacity: 0
            };
          }
          
          // PRIORITÉ 1 : Vérifier si c'est une daïra d'exception
          const isExceptionDaira = feature.getProperty('isExceptionDaira');
          if (isExceptionDaira) {
            const exceptionRegionId = feature.getProperty('regionId');
            if (exceptionRegionId) {
              const region = regionsMap.get(exceptionRegionId);
              if (region) {
                return {
                  fillColor: region.color,
                  fillOpacity: 0.6,
                  strokeColor: region.color,
                  strokeWeight: 2,
                  strokeOpacity: 0.8
                };
              }
            }
          }
          
          // Vérifier si c'est une daïra (sans exception)
          const isDaira = featureName.toLowerCase().includes('daïra');
          if (isDaira && !isExceptionDaira) {
            return {
              fillColor: 'transparent',
              fillOpacity: 0,
              strokeColor: 'transparent',
              strokeWeight: 0,
              strokeOpacity: 0
            };
          }
          
          // PRIORITÉ 2 : Styler les wilayas selon leur région culturelle
          const cultureId = feature.getProperty('culture');
          
          if (cultureId !== null && cultureId !== undefined && cultureId !== 0) {
            const region = regionsMap.get(cultureId);
            if (region) {
              const hasExceptionHoles = feature.getProperty('_hasExceptionHoles');
              
              if (hasExceptionHoles) {
                return {
                  fillColor: region.color,
                  fillOpacity: 0.6,
                  strokeColor: 'transparent',
                  strokeWeight: 0,
                  strokeOpacity: 0
                };
              } else {
                return {
                  fillColor: region.color,
                  fillOpacity: 0.6,
                  strokeColor: region.color,
                  strokeWeight: 2,
                  strokeOpacity: 0.8
                };
              }
            }
          }
          
          // Style par défaut
          if (isDaira) {
            return {
              fillColor: 'transparent',
              fillOpacity: 0,
              strokeColor: 'transparent',
              strokeWeight: 0,
              strokeOpacity: 0
            };
          }
          
          return {
            fillColor: 'transparent',
            fillOpacity: 0,
            strokeColor: 'transparent',
            strokeWeight: 0,
            strokeOpacity: 0
          };
        });
        
        // Attacher les événements
        mapInstance.current.data.addListener('click', (event: any) => {
          clickHandledByFeature = true;
          const clickedFeature = event.feature;
          
          const isExceptionDaira = clickedFeature.getProperty('isExceptionDaira');
          let region = null;
          
          if (isExceptionDaira) {
            const exceptionRegionId = clickedFeature.getProperty('regionId');
            if (exceptionRegionId) {
              region = regionsMap.get(exceptionRegionId);
            }
          }
          
          if (!region) {
            const cultureId = clickedFeature.getProperty('culture');
            if (cultureId !== null && cultureId !== undefined && cultureId !== 0) {
              region = regionsMap.get(cultureId);
            }
          }
          
          if (region) {
            handleOpenModal(region);
          }
          
          setTimeout(() => {
            clickHandledByFeature = false;
          }, 100);
        });
        
        mapInstance.current.addListener('click', (event: any) => {
          setTimeout(() => {
            if (!clickHandledByFeature && isPinnedRef.current) {
              handleCloseModal();
            }
          }, 150);
        });
        
        // Ajouter les noms des régions sur la carte
        requestAnimationFrame(() => {
          regionsCulturellesData.regions.forEach(region => {
            if (region.center) {
              new window.google.maps.Marker({
                position: { lat: region.center.lat, lng: region.center.lng },
                map: mapInstance.current,
                title: region.name,
                label: {
                  text: region.name,
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  fontFamily: 'Clash Display, sans-serif',
                  className: 'region-label'
                },
                icon: {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 0,
                  fillColor: 'transparent',
                  fillOpacity: 0,
                  strokeColor: 'transparent',
                  strokeWeight: 0
                }
              });
            }
          });
        });
      }
    }
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      if (mapInstance.current) {
        mapInstance.current.data.forEach((feature: any) => {
          mapInstance.current.data.remove(feature);
        });
        mapInstance.current = null;
      }
    };
  }, [isMobile, wilayasWithHolesGeoData, exceptionDairasGeoData, regionsMap]);

  const toggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        if (mapRef.current?.requestFullscreen) {
          await mapRef.current.requestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          await (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
    } catch (error) {
      console.error('Erreur plein écran:', error);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreenActive = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setIsFullscreen(isFullscreenActive);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const getCurrentRegion = () => {
    if (!selectedRegion) return null;
    const regionId = parseInt(selectedRegion, 10);
    return regionsMap.get(regionId) || null;
  };

  const handleOpenModal = (region: any) => {
    setSelectedRegion(region.id.toString());
    setIsPinned(true);
    isPinnedRef.current = true;
    
    const imageUrl = getRegionImage(region.id);
    const img = new Image();
    img.onload = () => {
      const maxHeight = 500;
      const aspectRatio = img.width / img.height;
      const calculatedWidth = Math.min(img.width, maxHeight * aspectRatio);
      setModalWidth(calculatedWidth + 40);
      setIsModalOpen(true);
    };
    img.src = imageUrl;
  };

  const handleCloseModal = () => {
    setSelectedRegion(null);
    setIsModalOpen(false);
    setModalWidth(null);
    setIsPinned(false);
    isPinnedRef.current = false;
  };

  useEffect(() => {
    isPinnedRef.current = isPinned;
  }, [isPinned]);

  // Mettre à jour les styles de la carte quand la région sélectionnée change
  useEffect(() => {
    if (mapInstance.current && regionsCulturellesData) {
      const currentSelectedRegion = selectedRegion ? regionsMap.get(parseInt(selectedRegion, 10)) : null;
      
      mapInstance.current.data.setStyle((feature: any) => {
        const featureName = feature.getProperty('name') || feature.getProperty('name:fr');
        if (!featureName) {
          return {
            fillColor: 'transparent',
            fillOpacity: 0,
            strokeColor: 'transparent',
            strokeWeight: 0,
            strokeOpacity: 0
          };
        }
        
        const isExceptionDaira = feature.getProperty('isExceptionDaira');
        if (isExceptionDaira) {
          const exceptionRegionId = feature.getProperty('regionId');
          if (exceptionRegionId) {
            const region = regionsMap.get(exceptionRegionId);
            if (region) {
              const isSelected = currentSelectedRegion && region.id === currentSelectedRegion.id;
              return {
                fillColor: region.color,
                fillOpacity: isSelected ? 0.8 : 0.6,
                strokeColor: region.color,
                strokeWeight: isSelected ? 4 : 2,
                strokeOpacity: isSelected ? 1 : 0.8
              };
            }
          }
        }
        
        const isDaira = featureName.toLowerCase().includes('daïra');
        if (!isDaira) {
          const cultureId = feature.getProperty('culture');
          const region = (cultureId !== null && cultureId !== undefined && cultureId !== 0)
            ? regionsMap.get(cultureId)
            : null;
          if (region) {
            const isSelected = currentSelectedRegion && region.id === currentSelectedRegion.id;
            const hasExceptionHoles = feature.getProperty('_hasExceptionHoles');
            
            if (hasExceptionHoles) {
              return {
                fillColor: region.color,
                fillOpacity: isSelected ? 0.8 : 0.6,
                strokeColor: 'transparent',
                strokeWeight: 0,
                strokeOpacity: 0
              };
            } else {
              return {
                fillColor: region.color,
                fillOpacity: isSelected ? 0.8 : 0.6,
                strokeColor: region.color,
                strokeWeight: isSelected ? 4 : 2,
                strokeOpacity: isSelected ? 1 : 0.8
              };
            }
          }
        }
        
        if (isDaira) {
          return {
            fillColor: 'transparent',
            fillOpacity: 0,
            strokeColor: 'transparent',
            strokeWeight: 0,
            strokeOpacity: 0
          };
        }
        
        return {
          fillColor: 'transparent',
          fillOpacity: 0,
          strokeColor: 'transparent',
          strokeWeight: 0,
          strokeOpacity: 0
        };
      });
    }
  }, [selectedRegion, regionsMap]);

  const currentRegion = getCurrentRegion();

  return (
    <MapContainer className="map-container" key="cultural-regions-map">
      <button
        onClick={toggleFullscreen}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 1000,
          background: 'rgba(255, 255, 255, 0.95)',
          border: '2px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          padding: '0',
          cursor: 'pointer',
          fontSize: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
          backdropFilter: 'blur(8px)',
          fontWeight: 'bold',
          color: '#333'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }}
        title={isFullscreen ? 'Sortir du plein écran' : 'Plein écran'}
      >
        {isFullscreen ? '⤢' : '⤡'}
      </button>

      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

      {currentRegion && (
        <SectionModal
          title={currentRegion.name}
          subtitle=""
          imageUrl={getRegionImage(currentRegion.id)}
          imageCaption={getRegionImageCaption(currentRegion.id)}
          sections={convertRegionToSections(currentRegion)}
          color={currentRegion.color}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          modalWidth={modalWidth}
        />
      )}
    </MapContainer>
  );
};

export default CulturalRegionsMap;

